import React, { useMemo } from 'react';
import { Box, Typography, IconButton, Tooltip, alpha, useTheme } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { toast } from 'react-toastify';

const escapeHtml = (str) =>
  String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Lightweight markdown: fenced code blocks, inline code, bold, links, paragraphs.
 */
export const parseDiscussionMarkdown = (text) => {
  const src = String(text ?? '');
  const tokens = [];
  const fenceRe = /```(\w*)\n?([\s\S]*?)```/g;
  let last = 0;
  let match;

  while ((match = fenceRe.exec(src)) !== null) {
    if (match.index > last) {
      tokens.push({ type: 'text', value: src.slice(last, match.index) });
    }
    tokens.push({ type: 'code', lang: match[1] || '', value: match[2].replace(/\n$/, '') });
    last = fenceRe.lastIndex;
  }
  if (last < src.length) {
    tokens.push({ type: 'text', value: src.slice(last) });
  }

  return tokens;
};

const formatInline = (line) => {
  let html = escapeHtml(line);
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
  );
  html = html.replace(/@([a-zA-Z0-9_]{2,32})/g, '<span class="disc-mention">@$1</span>');
  return html;
};

const TextBlock = ({ value, isDark }) => {
  const paragraphs = String(value ?? '').split(/\n{2,}/);
  return (
    <>
      {paragraphs.map((para, i) => {
        const lines = para.split('\n');
        const isList = lines.every((l) => /^[-*]\s/.test(l.trim()) || l.trim() === '');
        if (isList && lines.filter(Boolean).length) {
          return (
            <Box
              key={i}
              component="ul"
              sx={{ m: 0, pl: 2.5, mb: 1.25, '& li': { mb: 0.35 } }}
            >
              {lines
                .filter((l) => l.trim())
                .map((l, j) => (
                  <li
                    key={j}
                    dangerouslySetInnerHTML={{
                      __html: formatInline(l.replace(/^[-*]\s*/, '')),
                    }}
                  />
                ))}
            </Box>
          );
        }
        return (
          <Typography
            key={i}
            component="p"
            variant="body2"
            sx={{
              m: 0,
              mb: 1.25,
              lineHeight: 1.65,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              '& code': {
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                fontSize: '0.85em',
                px: 0.5,
                py: 0.15,
                borderRadius: 0.75,
                bgcolor: isDark ? alpha('#fff', 0.08) : alpha('#137274', 0.08),
              },
              '& strong': { fontWeight: 700 },
              '& a': { color: '#137274', textDecoration: 'underline' },
              '& .disc-mention': {
                color: '#137274',
                fontWeight: 700,
                bgcolor: isDark ? alpha('#137274', 0.2) : alpha('#137274', 0.1),
                px: 0.35,
                borderRadius: 0.5,
              },
            }}
            dangerouslySetInnerHTML={{ __html: formatInline(para) }}
          />
        );
      })}
    </>
  );
};

const CodeBlock = ({ value, lang, isDark }) => {
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => toast.success('Copied'));
  };

  return (
    <Box
      sx={{
        position: 'relative',
        mb: 1.5,
        borderRadius: 1.5,
        overflow: 'hidden',
        border: `1px solid ${isDark ? '#30363d' : 'rgba(15,60,70,0.12)'}`,
        '&:hover .copy-btn': { opacity: 1 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.25,
          py: 0.5,
          bgcolor: isDark ? '#21262d' : '#eef6f8',
          borderBottom: `1px solid ${isDark ? '#30363d' : 'rgba(15,60,70,0.1)'}`,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            fontSize: '0.65rem',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: isDark ? '#8b949e' : '#5f6b73',
          }}
        >
          {lang || 'code'}
        </Typography>
        <Tooltip title="Copy code">
          <IconButton size="small" className="copy-btn" onClick={copy} sx={{ opacity: 0.6, p: 0.35 }}>
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.25,
          overflow: 'auto',
          fontSize: '0.78rem',
          lineHeight: 1.5,
          bgcolor: isDark ? '#0d1117' : '#f8fafb',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        }}
      >
        <code>{value}</code>
      </Box>
    </Box>
  );
};

const DiscussionMarkdown = ({ content }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const tokens = useMemo(() => parseDiscussionMarkdown(content), [content]);

  return (
    <Box>
      {tokens.map((token, i) =>
        token.type === 'code' ? (
          <CodeBlock key={i} value={token.value} lang={token.lang} isDark={isDark} />
        ) : (
          <TextBlock key={i} value={token.value} isDark={isDark} />
        ),
      )}
    </Box>
  );
};

export default DiscussionMarkdown;
